import { User, UserRole } from '../types/user';
import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  getDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  setDoc 
} from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import {
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  getIdToken,
  sendPasswordResetEmail,
} from 'firebase/auth';

// Collection Firestore pour les utilisateurs
const USERS_COLLECTION = 'users';

// Initialisation des utilisateurs par défaut si nécessaire
const initDefaultUsers = async () => {
  if (typeof window !== 'undefined') {
    return;
  }
  const usersCollection = collection(db, USERS_COLLECTION);
  const querySnapshot = await getDocs(usersCollection);
  
  // Si aucun utilisateur n'existe, créer les utilisateurs par défaut
  if (querySnapshot.empty) {
    const defaultUsers = [
      {
        email: 'admin@livematch.com',
        password: 'admin123', // En production, utiliser des mots de passe hachés
        firstName: 'Admin',
        lastName: 'System',
        role: UserRole.ADMIN,
        status: 'active',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      {
        email: 'coach@livematch.com',
        password: 'coach123',
        firstName: 'Pierre',
        lastName: 'Dupont',
        role: UserRole.COACH,
        status: 'active',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    ];
    
    for (const user of defaultUsers) {
      await addDoc(usersCollection, user);
    }
  }
};

// Initialiser les utilisateurs par défaut au démarrage
initDefaultUsers().catch(console.error);

// Helper partagé pour construire ou créer l'utilisateur à partir d'un compte Google
const buildOrCreateUser = async (
  uid: string | undefined,
  email: string | null,
  displayName: string | null
): Promise<User> => {
  const usersCollectionRef = collection(db, USERS_COLLECTION);
  const qEmail = query(usersCollectionRef, where('email', '==', email || ''));
  const snapshot = await getDocs(qEmail);

  if (!snapshot.empty) {
    const docSnap = snapshot.docs[0];
    const data = docSnap.data() as User;
    return {
      id: docSnap.id,
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      role: data.role,
      status: data.status,
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
    } as User;
  }

  // Créer un utilisateur si inexistant
  const name = (displayName || '').trim();
  const parts = name.split(' ');
  const firstName = parts.length > 1 ? parts.slice(0, -1).join(' ') : name;
  const lastName = parts.length > 1 ? parts[parts.length - 1] : '';
  const now = new Date().toISOString();
  const newUserData: Omit<User, 'id'> = {
    email: email || '',
    firstName,
    lastName,
    role: UserRole.USER,
    status: 'inactive',
    club: '',
    phone: '',
    source: '',
    createdAt: now,
    updatedAt: now,
  };
  const userDocRef = doc(db, USERS_COLLECTION, uid || crypto.randomUUID());
  await setDoc(userDocRef, newUserData);
  return { id: userDocRef.id, ...newUserData } as User;
};

const ensureUserDoc = async (uid: string, data: Omit<User, 'id'>): Promise<User> => {
  const userDocRef = doc(db, USERS_COLLECTION, uid);
  const existing = await getDoc(userDocRef);
  if (existing.exists()) {
    const userData = existing.data() as User;
    return {
      id: uid,
      email: userData.email,
      firstName: userData.firstName,
      lastName: userData.lastName,
      role: userData.role,
      status: userData.status,
      createdAt: userData.createdAt,
      updatedAt: userData.updatedAt,
      club: userData.club,
      phone: userData.phone,
      source: userData.source,
    } as User;
  }

  await setDoc(userDocRef, data);
  return { id: uid, ...data } as User;
};

// Service d'authentification
const authService = {
  // Connexion utilisateur
  login: async (email: string, password: string): Promise<{ user: User; token: string } | null> => {
    try {
      const credential = await signInWithEmailAndPassword(auth, email, password);
      const uid = credential.user.uid;
      const now = new Date().toISOString();

      const user = await ensureUserDoc(uid, {
        email,
        firstName: '',
        lastName: '',
        role: UserRole.USER,
        status: 'active',
        club: '',
        phone: '',
        source: '',
        createdAt: now,
        updatedAt: now,
      });

      const token = await getIdToken(credential.user, true);
      return { user, token };
    } catch (error) {
      console.error("Erreur lors de la connexion:", error);
      return null;
    }
  },

  register: async (params: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
  }): Promise<{ user: User; token: string } | null> => {
    try {
      const { email, password, firstName, lastName } = params;
      const credential = await createUserWithEmailAndPassword(auth, email, password);
      const uid = credential.user.uid;
      const now = new Date().toISOString();

      const user = await ensureUserDoc(uid, {
        email,
        firstName,
        lastName,
        role: UserRole.USER,
        status: 'active',
        club: '',
        phone: '',
        source: 'email',
        createdAt: now,
        updatedAt: now,
      });

      const token = await getIdToken(credential.user, true);
      return { user, token };
    } catch (error) {
      console.error("Erreur lors de l'inscription:", error);
      return null;
    }
  },

  requestPasswordReset: async (email: string): Promise<boolean> => {
    try {
      await sendPasswordResetEmail(auth, email);
      return true;
    } catch (error) {
      console.error('Erreur lors de la demande de réinitialisation du mot de passe:', error);
      return false;
    }
  },

  // Connexion via Google
  loginWithGoogle: async (): Promise<User> => {
    const provider = new GoogleAuthProvider();

    // Si la page est cross-origin isolated (COOP/COEP), utiliser redirect flow directement
    try {
      if (typeof window !== 'undefined' && (window as any).crossOriginIsolated) {
        await signInWithRedirect(auth, provider);
        const redirectRes = await getRedirectResult(auth);
        if (!redirectRes) throw new Error('Google redirect result non disponible');
        const gUser = redirectRes.user;
        return await buildOrCreateUser(gUser.uid, gUser.email, gUser.displayName);
      }

      // Tenter le popup
      const result = await signInWithPopup(auth, provider);
      const gUser = result.user;
      return await buildOrCreateUser(gUser.uid, gUser.email, gUser.displayName);
    } catch (err: any) {
      // En cas de blocage COOP/COEP du popup ou d'autres erreurs liées aux popups, fallback sur redirect
      await signInWithRedirect(auth, provider);
      const redirectRes = await getRedirectResult(auth);
      if (!redirectRes) {
        throw err;
      }
      const gUser = redirectRes.user;
      return await buildOrCreateUser(gUser.uid, gUser.email, gUser.displayName);
    }

  },

  // Finaliser l'authentification Google après un signInWithRedirect
  completeLoginWithRedirect: async (): Promise<User | null> => {
    try {
      const redirectRes = await getRedirectResult(auth);
      if (!redirectRes) return null;
      const gUser = redirectRes.user;
      return await buildOrCreateUser(gUser.uid, gUser.email, gUser.displayName);
    } catch (e) {
      return null;
    }
  },

  // Déclenche uniquement la redirection Google (aucun popup)
  loginWithGoogleRedirect: async (): Promise<void> => {
    const provider = new GoogleAuthProvider();
    await signInWithRedirect(auth, provider);
  },

  // Vérifier si l'utilisateur est connecté
  getCurrentUser: (): User | null => {
    const userJson = localStorage.getItem('user');
    if (userJson) {
      return JSON.parse(userJson);
    }
    return null;
  },

  // Déconnexion
  logout: (): void => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
  },

  // Récupérer tous les utilisateurs (admin uniquement)
  getAllUsers: async (): Promise<User[]> => {
    try {
      const usersCollection = collection(db, USERS_COLLECTION);
      const querySnapshot = await getDocs(usersCollection);
      const usersList: User[] = [];
      
      querySnapshot.forEach((docSnapshot) => {
        const userData = docSnapshot.data() as User;
        // Créer un nouvel objet sans le mot de passe
        usersList.push({
          id: docSnapshot.id,
          email: userData.email,
          firstName: userData.firstName,
          lastName: userData.lastName,
          role: userData.role,
          status: userData.status,
          createdAt: userData.createdAt,
          updatedAt: userData.updatedAt
        });
      });
      
      return usersList;
    } catch (error) {
      console.error("Erreur lors de la récupération des utilisateurs:", error);
      return [];
    }
  },

  // Créer un nouvel utilisateur (admin uniquement)
  createUser: async (userData: Omit<User, 'id' | 'createdAt' | 'updatedAt'>): Promise<User> => {
    try {
      const usersCollection = collection(db, USERS_COLLECTION);
      
      // Vérifier si l'email existe déjà
      const q = query(usersCollection, where("email", "==", userData.email));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        throw new Error("Un utilisateur avec cet email existe déjà");
      }
      
      const newUser = {
        ...userData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      
      const docRef = await addDoc(usersCollection, newUser);
      
      // Créer un nouvel objet sans le mot de passe
      return {
        id: docRef.id,
        email: newUser.email,
        firstName: newUser.firstName,
        lastName: newUser.lastName,
        role: newUser.role,
        status: newUser.status,
        createdAt: newUser.createdAt,
        updatedAt: newUser.updatedAt
      };
    } catch (error) {
      console.error("Erreur lors de la création de l'utilisateur:", error);
      throw error;
    }
  },

  // Mettre à jour un utilisateur (admin uniquement)
  updateUser: async (id: string, userData: Partial<User>): Promise<User | null> => {
    try {
      const userRef = doc(db, USERS_COLLECTION, id);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        return null;
      }
      
      const updatedUser = {
        ...userData,
        updatedAt: new Date().toISOString()
      };
      
      await updateDoc(userRef, updatedUser);
      
      // Récupérer l'utilisateur mis à jour
      const updatedUserDoc = await getDoc(userRef);
      const updatedUserData = updatedUserDoc.data() as User;
      
      // Créer un nouvel objet sans le mot de passe
      return {
        id: updatedUserDoc.id,
        email: updatedUserData.email,
        firstName: updatedUserData.firstName,
        lastName: updatedUserData.lastName,
        role: updatedUserData.role,
        status: updatedUserData.status,
        createdAt: updatedUserData.createdAt,
        updatedAt: updatedUserData.updatedAt
      };
    } catch (error) {
      console.error("Erreur lors de la mise à jour de l'utilisateur:", error);
      return null;
    }
  },

  // Supprimer un utilisateur (admin uniquement)
  deleteUser: async (id: string): Promise<boolean> => {
    try {
      const userRef = doc(db, USERS_COLLECTION, id);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        return false;
      }
      
      await deleteDoc(userRef);
      return true;
    } catch (error) {
      console.error("Erreur lors de la suppression de l'utilisateur:", error);
      return false;
    }
  },

  // Changer le statut d'un utilisateur (activer/suspendre) (admin uniquement)
  changeUserStatus: async (id: string, status: 'active' | 'suspended'): Promise<User | null> => {
    try {
      const userRef = doc(db, USERS_COLLECTION, id);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        return null;
      }
      
      const updateData = {
        status,
        updatedAt: new Date().toISOString()
      };
      
      await updateDoc(userRef, updateData);
      
      // Récupérer l'utilisateur mis à jour
      const updatedUserDoc = await getDoc(userRef);
      const updatedUserData = updatedUserDoc.data() as User;
      
      // Créer un nouvel objet sans le mot de passe
      return {
        id: updatedUserDoc.id,
        email: updatedUserData.email,
        firstName: updatedUserData.firstName,
        lastName: updatedUserData.lastName,
        role: updatedUserData.role,
        status: updatedUserData.status,
        createdAt: updatedUserData.createdAt,
        updatedAt: updatedUserData.updatedAt
      };
    } catch (error) {
      console.error("Erreur lors du changement de statut de l'utilisateur:", error);
      return null;
    }
  },
  
  // Réinitialiser le mot de passe d'un utilisateur (admin uniquement)
  resetPassword: async (id: string): Promise<{ success: boolean, temporaryPassword?: string }> => {
    try {
      const userRef = doc(db, USERS_COLLECTION, id);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        return { success: false };
      }
      
      // Générer un mot de passe temporaire aléatoire
      const temporaryPassword = Math.random().toString(36).slice(-8);
      
      const updateData = {
        password: temporaryPassword,
        updatedAt: new Date().toISOString()
      };
      
      await updateDoc(userRef, updateData);
      
      return { success: true, temporaryPassword };
    } catch (error) {
      console.error("Erreur lors de la réinitialisation du mot de passe:", error);
      return { success: false };
    }
  }
};

export default authService;
